import { useState, useEffect, useCallback } from 'react'
import { create } from 'zustand'

interface ToastItem {
  id: number
  message: string
  type: 'info' | 'success' | 'error'
}

interface ToastState {
  toasts: ToastItem[]
  addToast: (message: string, type?: 'info' | 'success' | 'error') => void
  removeToast: (id: number) => void
}

let nextId = 0

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, type = 'info') => {
    const id = nextId++
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }))
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, 4000)
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

export function toast(message: string, type?: 'info' | 'success' | 'error') {
  useToastStore.getState().addToast(message, type)
}

function ToastItem({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [exiting, setExiting] = useState(false)

  const dismiss = useCallback(() => {
    setExiting(true)
    setTimeout(onDismiss, 200)
  }, [onDismiss])

  useEffect(() => {
    const timer = setTimeout(dismiss, 3800)
    return () => clearTimeout(timer)
  }, [dismiss])

  const colors = {
    info: 'border-l-[rgb(var(--np-accent))] bg-[rgb(var(--np-accent)/0.05)]',
    success: 'border-l-[rgb(var(--np-status-ok))] bg-[rgb(var(--np-status-ok)/0.05)]',
    error: 'border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)]',
  }

  return (
    <div
      className={`border-l-2 px-4 py-3 rounded-r-lg shadow-md bg-[rgb(var(--np-surface))] min-w-64 max-w-sm ${colors[item.type]} ${
        exiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
      } transition-all duration-200 cursor-pointer animate-slide-in`}
      onClick={dismiss}
    >
      <p className="text-sm text-[rgb(var(--np-tx-primary))]">{item.message}</p>
    </div>
  )
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts)
  const removeToast = useToastStore((s) => s.removeToast)

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.id} item={t} onDismiss={() => removeToast(t.id)} />
      ))}
    </div>
  )
}
